import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
    FloppyDisk, Eye, Printer, SpinnerGap, ArrowsCounterClockwise, Receipt,
} from '@phosphor-icons/react';
import { buildReceiptHtml, printReceiptInline } from '../lib/receiptHtml';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const DEFAULT_CONFIG = {
    show_logo: true, show_company_name: true, show_rfc: true,
    show_address: true, show_phone: true, show_email: true,
    header_message: '',
    show_date: true, show_seller: true, show_customer_name: true,
    show_customer_phone: false, show_customer_email: false,
    show_folio_per_item: true, show_qr_per_item: true,
    qr_caption: 'Escanea para ver tu certificado',
    show_destination: false,
    show_total: true, show_payment_method: true, show_subtotal_breakdown: false,
    footer_message: '¡Gracias por tu compra!',
    show_website: true, show_internal_folio: true,
    extra_footer_note: '',
    paper_width_mm: 58, font_size: 13,
};

// Sample sale used for the live preview pane
const SAMPLE_SALE = {
    id: 'demo-12345678-abcd-ef00-1234-567890abcdef',
    total_amount: 9499,
    payment_method: 'cash',
    created_at: new Date().toISOString(),
    customer: { name: 'María García López', email: 'maria@example.com', phone: '+52 55 1234 5678' },
    seller: { name: 'Vendedor Demo' },
    items: [
        { folio: 'CC-260301-0042', product_name: 'Escapada 4 Estrellas Riviera Maya', unit_price: 4999, product: { destination: 'Riviera Maya' } },
        { folio: 'CC-260301-0043', product_name: 'Spa & Wellness Day', unit_price: 4500, product: { destination: 'Cancún' } },
    ],
};

// Reusable toggle row
const Toggle = ({ label, hint, checked, onChange, testid }) => (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-[#F4ECE0] last:border-b-0">
        <div className="min-w-0 flex-1">
            <Label className="text-sm text-[#1F2D4B] font-medium">{label}</Label>
            {hint && <p className="text-[11px] text-[#8B8474] mt-0.5">{hint}</p>}
        </div>
        <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
);

const Section = ({ title, children }) => (
    <div className="bg-white rounded-2xl border border-[#ECE3D2] p-4 sm:p-5">
        <h3 className="text-xs uppercase tracking-wider text-[#8B8474] font-semibold mb-2">{title}</h3>
        <div>{children}</div>
    </div>
);

const ReceiptConfigPage = () => {
    const [config, setConfig] = useState(DEFAULT_CONFIG);
    const [company, setCompany] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [printing, setPrinting] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const [rc, c] = await Promise.all([
                    axios.get(`${API}/company/receipt-config`, { headers: authHeaders() }),
                    axios.get(`${API}/company`, { headers: authHeaders() }),
                ]);
                setConfig({ ...DEFAULT_CONFIG, ...(rc.data || {}) });
                setCompany(c.data || {});
            } catch {
                toast.error('No se pudo cargar la configuración');
            } finally { setLoading(false); }
        })();
    }, []);

    const set = (key) => (value) => setConfig((cfg) => ({ ...cfg, [key]: value }));

    const handleSave = async () => {
        setSaving(true);
        try {
            // Normalize empty strings to null where appropriate
            const payload = { ...config };
            if (payload.header_message === '') payload.header_message = null;
            if (payload.extra_footer_note === '') payload.extra_footer_note = null;
            await axios.put(`${API}/company/receipt-config`, payload, { headers: authHeaders() });
            toast.success('Configuración guardada');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'No se pudo guardar');
        } finally { setSaving(false); }
    };

    const handleReset = () => {
        if (window.confirm('¿Restablecer a los valores predeterminados?')) {
            setConfig(DEFAULT_CONFIG);
        }
    };

    const handleTestPrint = async () => {
        setPrinting(true);
        try {
            await printReceiptInline({
                sale: SAMPLE_SALE,
                company,
                config,
                appUrl: window.location.origin,
            });
        } catch {
            toast.error('No se pudo abrir el diálogo de impresión');
        } finally {
            setTimeout(() => setPrinting(false), 800);
        }
    };

    const previewHtml = useMemo(
        () => buildReceiptHtml({
            sale: SAMPLE_SALE,
            company,
            config,
            appUrl: window.location.origin,
        }),
        [company, config]
    );

    if (loading) return <div className="p-8 text-center text-[#8B8474]">Cargando configuración…</div>;

    return (
        <div className="space-y-6" data-testid="receipt-config-page">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <p className="text-[10px] uppercase tracking-[0.18em] text-[#8B8474] font-semibold mb-1">
                        Configuración
                    </p>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#1F2D4B] flex items-center gap-2">
                        <Receipt size={28} weight="duotone" className="text-[#FB7185]" />
                        Recibo de venta
                    </h1>
                    <p className="text-sm text-[#4B5878] mt-1">
                        Decide qué información aparece en el ticket impreso. Los cambios se aplican
                        a partir de la siguiente venta.
                    </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <Button
                        variant="outline"
                        onClick={handleReset}
                        data-testid="reset-defaults-btn"
                        className="h-10 rounded-md border-[#ECE3D2] text-[#4B5878] hover:bg-white"
                    >
                        <ArrowsCounterClockwise size={14} weight="bold" className="mr-1.5" />
                        Restablecer
                    </Button>
                    <Button
                        variant="outline"
                        onClick={handleTestPrint}
                        disabled={printing}
                        data-testid="test-print-btn"
                        className="h-10 rounded-md border-[#1F2D4B] text-[#1F2D4B] hover:bg-[#F4ECE0]"
                    >
                        {printing
                            ? <SpinnerGap size={14} className="animate-spin mr-1.5" />
                            : <Printer size={14} weight="bold" className="mr-1.5" />}
                        Imprimir prueba
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={saving}
                        data-testid="save-config-btn"
                        className="h-10 bg-[#1F2D4B] hover:bg-[#2E3F60] text-white rounded-md disabled:opacity-60"
                    >
                        {saving
                            ? <SpinnerGap size={14} className="animate-spin mr-1.5" />
                            : <FloppyDisk size={14} weight="bold" className="mr-1.5" />}
                        Guardar
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_minmax(260px,380px)] gap-5">
                {/* LEFT — toggles */}
                <div className="space-y-4">
                    <Section title="Encabezado">
                        <Toggle label="Logo de la empresa" hint="Sólo si la empresa tiene logo configurado" checked={config.show_logo} onChange={set('show_logo')} testid="toggle-show_logo" />
                        <Toggle label="Nombre / razón social" checked={config.show_company_name} onChange={set('show_company_name')} testid="toggle-show_company_name" />
                        <Toggle label="RFC" checked={config.show_rfc} onChange={set('show_rfc')} testid="toggle-show_rfc" />
                        <Toggle label="Dirección" checked={config.show_address} onChange={set('show_address')} testid="toggle-show_address" />
                        <Toggle label="Teléfono" checked={config.show_phone} onChange={set('show_phone')} testid="toggle-show_phone" />
                        <Toggle label="Correo" checked={config.show_email} onChange={set('show_email')} testid="toggle-show_email" />
                        <div className="pt-3 mt-2 border-t border-[#F4ECE0]">
                            <Label className="text-xs uppercase tracking-wider text-[#8B8474] font-semibold">
                                Mensaje al inicio (opcional)
                            </Label>
                            <input
                                value={config.header_message || ''}
                                onChange={(e) => set('header_message')(e.target.value)}
                                placeholder="Ej. Sucursal Reforma · Bienvenido"
                                data-testid="input-header_message"
                                className="w-full h-10 px-3 mt-1 rounded-md border border-[#ECE3D2] text-sm focus:outline-none focus:ring-2 focus:ring-[#1F2D4B]/20 focus:border-[#1F2D4B]"
                            />
                        </div>
                    </Section>

                    <Section title="Metadatos de la venta">
                        <Toggle label="Fecha y hora" checked={config.show_date} onChange={set('show_date')} testid="toggle-show_date" />
                        <Toggle label="Nombre del vendedor" checked={config.show_seller} onChange={set('show_seller')} testid="toggle-show_seller" />
                        <Toggle label="Nombre del cliente" checked={config.show_customer_name} onChange={set('show_customer_name')} testid="toggle-show_customer_name" />
                        <Toggle label="Teléfono del cliente" checked={config.show_customer_phone} onChange={set('show_customer_phone')} testid="toggle-show_customer_phone" />
                        <Toggle label="Correo del cliente" checked={config.show_customer_email} onChange={set('show_customer_email')} testid="toggle-show_customer_email" />
                    </Section>

                    <Section title="Certificados / artículos">
                        <Toggle label="Mostrar folio por certificado" checked={config.show_folio_per_item} onChange={set('show_folio_per_item')} testid="toggle-show_folio_per_item" />
                        <Toggle label="Imprimir código QR por certificado" hint="El cliente lo escanea para abrir su certificado" checked={config.show_qr_per_item} onChange={set('show_qr_per_item')} testid="toggle-show_qr_per_item" />
                        <Toggle label="Mostrar destino del producto" checked={config.show_destination} onChange={set('show_destination')} testid="toggle-show_destination" />
                        <div className="pt-3 mt-2 border-t border-[#F4ECE0]">
                            <Label className="text-xs uppercase tracking-wider text-[#8B8474] font-semibold">
                                Texto debajo del QR
                            </Label>
                            <input
                                value={config.qr_caption || ''}
                                onChange={(e) => set('qr_caption')(e.target.value)}
                                placeholder="Escanea para ver tu certificado"
                                data-testid="input-qr_caption"
                                disabled={!config.show_qr_per_item}
                                className="w-full h-10 px-3 mt-1 rounded-md border border-[#ECE3D2] text-sm focus:outline-none focus:ring-2 focus:ring-[#1F2D4B]/20 focus:border-[#1F2D4B] disabled:opacity-50 disabled:bg-[#F9F7F2]"
                            />
                        </div>
                    </Section>

                    <Section title="Totales y pago">
                        <Toggle label="Mostrar total" checked={config.show_total} onChange={set('show_total')} testid="toggle-show_total" />
                        <Toggle label="Forma de pago" checked={config.show_payment_method} onChange={set('show_payment_method')} testid="toggle-show_payment_method" />
                    </Section>

                    <Section title="Pie / Footer">
                        <div className="pt-1 pb-3">
                            <Label className="text-xs uppercase tracking-wider text-[#8B8474] font-semibold">
                                Mensaje principal
                            </Label>
                            <input
                                value={config.footer_message || ''}
                                onChange={(e) => set('footer_message')(e.target.value)}
                                placeholder="¡Gracias por tu compra!"
                                data-testid="input-footer_message"
                                className="w-full h-10 px-3 mt-1 rounded-md border border-[#ECE3D2] text-sm focus:outline-none focus:ring-2 focus:ring-[#1F2D4B]/20 focus:border-[#1F2D4B]"
                            />
                        </div>
                        <Toggle label="Mostrar sitio web" hint="Tomado de la configuración de empresa" checked={config.show_website} onChange={set('show_website')} testid="toggle-show_website" />
                        <Toggle label="Mostrar folio interno de la venta" hint="Para soporte y auditoría" checked={config.show_internal_folio} onChange={set('show_internal_folio')} testid="toggle-show_internal_folio" />
                        <div className="pt-3 mt-2 border-t border-[#F4ECE0]">
                            <Label className="text-xs uppercase tracking-wider text-[#8B8474] font-semibold">
                                Nota adicional (opcional)
                            </Label>
                            <textarea
                                value={config.extra_footer_note || ''}
                                onChange={(e) => set('extra_footer_note')(e.target.value)}
                                placeholder="Ej. Política de devolución, redes sociales, T&C, próxima promo…"
                                data-testid="input-extra_footer_note"
                                className="w-full min-h-[80px] p-3 mt-1 rounded-md border border-[#ECE3D2] text-sm focus:outline-none focus:ring-2 focus:ring-[#1F2D4B]/20 focus:border-[#1F2D4B]"
                            />
                        </div>
                    </Section>

                    <Section title="Formato del papel">
                        <div className="grid grid-cols-2 gap-4 pt-1">
                            <div>
                                <Label className="text-xs uppercase tracking-wider text-[#8B8474] font-semibold">Ancho</Label>
                                <select
                                    value={config.paper_width_mm}
                                    onChange={(e) => set('paper_width_mm')(parseInt(e.target.value, 10))}
                                    data-testid="select-paper_width_mm"
                                    className="w-full h-10 px-2 mt-1 rounded-md border border-[#ECE3D2] text-sm focus:outline-none focus:ring-2 focus:ring-[#1F2D4B]/20 focus:border-[#1F2D4B] bg-white"
                                >
                                    <option value={58}>58 mm (térmica estándar)</option>
                                    <option value={80}>80 mm (térmica ancha)</option>
                                </select>
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-wider text-[#8B8474] font-semibold">Tamaño de fuente</Label>
                                <select
                                    value={config.font_size}
                                    onChange={(e) => set('font_size')(parseInt(e.target.value, 10))}
                                    data-testid="select-font_size"
                                    className="w-full h-10 px-2 mt-1 rounded-md border border-[#ECE3D2] text-sm focus:outline-none focus:ring-2 focus:ring-[#1F2D4B]/20 focus:border-[#1F2D4B] bg-white"
                                >
                                    {[9, 10, 11, 12, 13, 14].map((n) => (
                                        <option key={n} value={n}>{n} px</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </Section>
                </div>

                {/* RIGHT — live preview */}
                <div className="lg:sticky lg:top-4 self-start">
                    <div className="bg-white rounded-2xl border border-[#ECE3D2] p-4">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-xs uppercase tracking-wider text-[#8B8474] font-semibold flex items-center gap-1.5">
                                <Eye size={13} weight="duotone" /> Vista previa
                            </h3>
                            <span className="text-[10px] text-[#8B8474]">Datos de muestra</span>
                        </div>
                        <div
                            className="bg-[#F9F7F2] rounded-md p-2 max-h-[70vh] overflow-y-auto flex justify-center"
                            data-testid="receipt-preview"
                        >
                            <iframe
                                title="Receipt preview"
                                srcDoc={previewHtml}
                                style={{
                                    border: '1px solid #ECE3D2',
                                    width: `${config.paper_width_mm * 3.78}px`, // mm to px approx
                                    height: '70vh',
                                    background: '#fff',
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReceiptConfigPage;
